/* eslint-disable max-len */
import React from 'react';
import {
  makeStyles,
  Box,
  Container,
  Button,
  useTheme,
  useMediaQuery,
  Typography,
} from '@material-ui/core';
import Scrollspy from 'react-scrollspy';
import ScrollTo from 'utils/ScrollTo';
import DisplayMoney from 'utils/displayMoney';
import { withTranslation } from 'config/i18n';

const useStyles = makeStyles((theme) => ({
  stickyBar: {
    backgroundColor: theme.palette.common.white,
    position: 'sticky',
    top: '0',
    zIndex: '3',
    boxShadow: '0 2px 4px 0 rgb(0 0 0 / 12%)',

    [theme.breakpoints.down('md')]: {
      top: 'calc(100% - 53px)',
      padding: theme.spacing(1, 0),
    },
    [theme.breakpoints.down('sm')]: {
      top: 'calc(100% - 68px)',
    },
  },
  stickyBarInner: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  tabBar: {
    boxShadow: 'none',
    top: '0',
    listStyle: 'none',
    padding: '0',
    margin: '0',
    overflow: 'hidden',
    overflowX: 'auto',
    marginBottom: '-1px',

    [theme.breakpoints.down('sm')]: {
      maxWidth: 'calc(100% - 55px)',
    },

    '& > li': {
      display: 'inline-block',

      [theme.breakpoints.down('md')]: {
        marginBottom: '0',
      },

      '&:not(:last-child)': {
        marginRight: theme.spacing(3.75),
      },
    },
  },
  tabBarBtn: {
    display: 'block',
    padding: theme.spacing(2, 0, 2.5),
    fontSize: theme.typography.pxToRem(14),
    textDecoration: 'none',
    color: theme.palette.grey['300'],
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    lineHeight: theme.typography.pxToRem(16),

    '.selected &': {
      color: theme.palette.common.black,
      fontWeight: '600',
      borderBottom: `2px solid ${theme.palette.common.black}`,
    },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',

    [theme.breakpoints.down('md')]: {
      flexGrow: '1',
      justifyContent: 'space-between',
    },
  },
  prices: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  pricesInrow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    minHeight: theme.spacing(6.25),
  },
  priceDashed: {
    textDecoration: 'line-through',
    marginRight: theme.spacing(1),
    fontWeight: '400',
  },
  actionBtn: {
    marginLeft: theme.spacing(1.5),
  },
  actionsInfo: {
    '&:nth-child(2)': {
      paddingLeft: theme.spacing(1),
      marginLeft: theme.spacing(1),
      borderLeft: `1px solid ${theme.palette.grey['100']}`,
    },
  },
}));

const StickyBar = (props) => {
  const theme = useTheme();
  const classes = useStyles();
  const {
    t,
    openSummaryDialog,
    bookingData,
    price,
    vehicleQuoteId,
    pricingOption,
    hourlyPrice,
    showReserveBtn = true,
    vehicleDetails,
  } = props;

  const isHostedHourly = pricingOption === 'hourly' || pricingOption === 'daily_and_hourly';
  const isHostedDaily = pricingOption === 'daily' || pricingOption === 'daily_and_hourly';
  const isLgScreen = useMediaQuery(theme.breakpoints.up('lg'));

  // Hide entire bar on mobile when bookings are disabled
  if (!isLgScreen && vehicleDetails?.booking_disabled) {
    return null;
  }

  const stickyBarItems = [
    {
      label: t('fe_es_vehicle_page:description'),
      id: 'vehicle_description',
    },
    {
      label: t('fe_es_vehicle_page:host'),
      id: 'vehicle_host',
    },
    {
      label: t('fe_es_vehicle_page:technical_details'),
      id: 'vehicle_technical_details',
    },
    {
      label: t('fe_es_vehicle_page:reviews'),
      id: 'vehicle_reviews',
    },
  ];

  return (
    <>
      <Box className={classes.stickyBar}>
        <Container>
          <Box className={classes.stickyBarInner}>
            {isLgScreen && (
              <Scrollspy
                className={classes.tabBar}
                items={stickyBarItems.map((item) => item.id)}
                currentClassName="selected"
                offset={-60}
              >
                {stickyBarItems.map((item) => (
                  <li key={item.label}>
                    <button
                      type="button"
                      className={classes.tabBarBtn}
                      onClick={() => ScrollTo(item.id, -100)}
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </Scrollspy>
            )}

            {showReserveBtn && !vehicleDetails?.booking_disabled && (
            <Box className={classes.actions}>
              {isHostedDaily && (
              <Box className={classes.actionsInfo}>
                {bookingData
                  && bookingData.quote
                  && bookingData.quote.price_per_day
                  && bookingData.quote.price_per_day.before
                    !== bookingData.quote.price_per_day.after ? (
                      <Typography variant="h5" className={classes.prices}>
                        <span className={classes.priceDashed}>
                          {DisplayMoney(bookingData.quote.price_per_day.before)}
                        </span>
                        <span>
                          <span>{DisplayMoney(bookingData.quote.price_per_day.after)}</span>
                          <span>{t('fe_es_vehicle_page:per_day')}</span>
                        </span>
                      </Typography>
                  ) : (
                    <Typography variant="h5" className={classes.pricesInrow}>
                      <span className={classes.price}>{price && DisplayMoney(price.day)}</span>
                      <span>{t('fe_es_vehicle_page:per_day')}</span>
                    </Typography>
                  )}
              </Box>
              )}

              {isHostedHourly && !!hourlyPrice && (
              <Box className={classes.actionsInfo}>
                <Box>
                  <>
                    <Typography variant="h5" component="div" className={classes.pricesInrow}>
                      <span>{DisplayMoney(hourlyPrice)}</span>
                      <span>{t('fe_es_vehicle_page:per_hour')}</span>
                    </Typography>
                  </>
                </Box>
              </Box>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={isLgScreen ? () => ScrollTo(vehicleQuoteId, -90) : openSummaryDialog}
                className={classes.actionBtn}
              >
                {t('fe_es_vehicle_page:reserve_now')}
              </Button>

            </Box>
            )}
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default withTranslation('fe_es_vehicle_page')(StickyBar);